// All product page content in one place.
// Copy follows the approved branding brief. Positioning claims must not be altered here
// without approval. House rules: no em-dashes anywhere, and the public name of the
// clinical skills product is Practicums (PracticumsPro for the premium tier).
//
// MEDIA SLOTS: each product has a `media` object. To publish a demo film, set
// `video` to an object like:
//   { src: 'https://firebasestorage.googleapis.com/.../demo.mp4', poster: '/screenshots/x.png', caption: 'Two minute walkthrough' }
// or, for YouTube/Vimeo:
//   { embed: 'https://www.youtube.com/embed/VIDEO_ID', caption: 'Two minute walkthrough' }
// Leave `video: null` to show the designed "film in production" slot instead.

const products = [
  {
    slug: 'suites',
    name: 'EdAI Suites',
    audienceTag: 'For certification boards',
    headline: 'Certification infrastructure, from designated sources to defensible exams.',
    subhead:
      "Item generation, learning management, oral examination simulation, MOC portfolio, and secure testing, built on your board's own designated content.",
    body: [
      "EdAI Suites turns a board's designated sources into certification-grade examination items with 87% less expert physician time, while meeting or exceeding traditional psychometric benchmarks.",
      'Every item that could carry consequence is decided by a human: classifiers triage and order expert attention, but never gate or decide. Live in high-stakes examinations since 2025, with zero AI-attributed factual errors and zero validity challenges from regulators or accreditors.',
    ],
    features: [
      {
        title: 'Certification-grade item generation',
        text: 'Items calibrated across the full expertise spectrum, from resident foundation to subspecialty focus, generated from the sources your board designates and nothing else.',
      },
      {
        title: 'A complete audit trail',
        text: 'Every item traces from designated source content to the approved item, claim by claim. When an item is challenged, the chain is the answer.',
      },
      {
        title: 'One integrated suite',
        text: 'LMS, oral exam simulation, MOC portfolio, and a secure testing platform (in development), operating on the same validated corpus.',
      },
    ],
    stats: [
      { value: '87%', label: 'Reduction in expert physician time per item' },
      { value: '0', label: 'AI-attributed factual errors in production' },
      { value: '100%', label: 'Client retention' },
    ],
    deepDive: {
      heading: 'What is inside the suite',
      intro:
        'Suites is not a single tool. It is the working infrastructure of a certifying board, organized as modules on one corpus and one audit trail.',
      items: [
        { title: 'Item generation', text: 'Multiple-choice item drafting from designated sources, triaged and ordered for expert review. The classifier prioritizes attention; the physician decides.' },
        { title: 'Oral exam preparation and simulation', text: 'Structured oral examination practice built on the same content that the written examination draws from.' },
        { title: 'Learning management', text: 'A purpose-built medical LMS: courses, learning paths with prerequisite sequencing, CME credit allocation, certificates with verification codes, and instructor analytics.' },
        { title: 'MOC and CME tracking', text: 'Continuous competency portfolios and credit tracking for diplomates, connected to the same corpus they were examined on.' },
        { title: 'Fact checking and content review', text: 'Reviewer workflows that hold every claim against its designated source before it enters the item pool.' },
        { title: 'Secure testing platform', text: 'In development: delivery, proctoring workflows, and biometric identity, designed for the scrutiny a certifying examination carries.' },
      ],
    },
    exampleHeading: 'An item, with its chain',
    example:
      'A generated item enters the review queue ranked by a classifier, carrying its source passages alongside it. An expert physician reviews the stem, the options, and the key against those passages, edits or approves, and the decision is recorded. The published item can be traced back to the designated source sentence it came from. That chain is what makes an exam defensible.',
    media: {
      video: null,
      videoTitle: 'See the item pipeline',
      videoText: 'A walkthrough of the path from designated source to approved examination item: generation, triage, expert review, and the audit trail.',
      screenshots: [
        { src: '/screenshots/generate-mcq.png', alt: 'Item generation interface', caption: 'Item generation from designated sources' },
        { src: '/screenshots/item_bank.png', alt: 'Item bank management', caption: 'The item bank, with review status' },
        { src: '/screenshots/mod2-orals-prep.png', alt: 'Oral exam preparation module', caption: 'Oral examination preparation' },
        { src: '/screenshots/lms1.png', alt: 'Learning management system', caption: 'The medical LMS' },
        { src: '/screenshots/analyze-dashboard.png', alt: 'Analytics dashboard', caption: 'Program analytics' },
        { src: '/screenshots/administer-proctoring.png', alt: 'Exam administration', caption: 'Examination administration' },
      ],
    },
    cta: { label: 'Talk to us about your board', to: '/contact' },
    related: ['prep', 'medschool'],
  },
  {
    slug: 'prep',
    name: 'EdAI Prep',
    audienceTag: 'For candidates',
    headline: 'Prepare against the same validated corpus your exam is built from.',
    subhead:
      'Spaced-repetition study and calibrated practice, scoped to what your board or jurisdiction actually requires.',
    body: [
      'EdAI Prep delivers preparation built on authority-derived content: the sources your board designates, the blueprint your jurisdiction publishes. A modern spaced-repetition scheduler (FSRS) and source-weighted concept mastery tracking focus every study session on what you are accountable for, calibrated just above your current capability.',
      'The architecture works for any jurisdiction with a blueprint: blueprint in, validated preparation out. Today that means clinical-case questions, timed mock exams, flashcards, source reading, and an adaptive plan across licensing exams in the United States, Canada, the United Kingdom, Australia, and Ireland, with Mexico in active development.',
    ],
    features: [
      {
        title: 'FSRS spaced repetition',
        text: 'A modern scheduler with concept mastery tracking, so review time goes where your recall is weakest and your blueprint weight is highest.',
      },
      {
        title: 'Calibrated practice items',
        text: 'Practice questions built to certification-exam style and difficulty, each traceable to a certified claim with the certifying physician and source one click away.',
      },
      {
        title: 'Any jurisdiction with a blueprint',
        text: 'Multi-jurisdiction licensing preparation from the same architecture: different drug names, reference ranges, and referral thresholds, kept in their own lanes.',
      },
    ],
    stats: null,
    deepDive: {
      heading: 'Verified before it reaches you',
      intro:
        'Every generated item passes automated integrity checks before it is ever served. Generation from a certified source is the start; verification is the point.',
      items: [
        { title: 'Vignette coherence', text: 'A separate review pass (never the model that wrote the question) reads each multi-question case and asks whether a real physician would find the patient course clinically plausible. Incoherent cases are rejected or trimmed before a learner sees them.' },
        { title: 'Grounding integrity', text: 'For every explanation of why an option is wrong, an independent judge verifies that the cited certified claim actually establishes it. Explanations that merely sound authoritative do not pass.' },
        { title: 'Structural defensibility', text: 'If the certified evidence cannot cleanly support one single best answer, the item is pulled rather than shipped with a manufactured key.' },
        { title: 'Continuous re-audit', text: 'The live question pool is re-audited on a standing basis, not just checked once at generation time. A validated item is cached and reused, so integrity does not cost speed.' },
      ],
    },
    exampleHeading: 'What a study day looks like',
    example:
      'Your plan surfaces the concepts your exam weights most and your recall holds least. You work a timed clinical case, read why each wrong option is wrong, and open the source review behind the answer, with the certifying physician named. The flashcards you miss come back on an FSRS schedule tuned to you. No pass-probability promises: readiness is projected honestly from your own performance.',
    media: {
      video: null,
      videoTitle: 'See a study session',
      videoText: 'A walkthrough of the adaptive plan, a clinical case with sourced explanations, and the spaced-repetition review flow.',
      screenshots: [
        { src: '/screenshots/prep-exams.jpg', alt: 'EdAI Prep written exam catalog', caption: 'One account, every jurisdiction: written exams from USMLE and MCCQE to UKMLA, AMC, and ENARM, with skills exams next' },
      ],
    },
    cta: { label: 'Start preparing', to: '/contact' },
    related: ['pathways', 'express'],
  },
  {
    slug: 'medschool',
    name: 'EdAI MedSchool',
    audienceTag: 'For medical schools',
    headline: 'The content-driven architecture, extended into medical education.',
    subhead:
      'Teach, assess, and document how well your students work with clinical knowledge, and with clinical AI.',
    body: [
      'EdAI MedSchool brings authority-derived content into undergraduate medical education, with PreRounds, a clinical AI safety training platform, as its spine. It teaches students and physicians to use clinical AI without being used by it, and produces the evidence trail a school needs to show an accreditor it did so.',
      'Students learn against the same validated knowledge they will eventually be licensed and certified on.',
    ],
    features: [
      {
        title: 'PreRounds: clinical AI safety training',
        text: 'Learners work realistic cases alongside an AI assistant while TheDude, a real-time intervention engine, watches for the failure modes that actually hurt patients: contaminating the model with a premature diagnosis, dropping socioeconomic context between queries, trusting confident output without probing its uncertainty, and missing the moment the AI drifts outside its competence.',
      },
      {
        title: 'Clinical AI competency, documented',
        text: 'Instruction and assessment across the knowledge, skills, and judgment of working with clinical AI, with the documentation trail accreditation review asks for.',
      },
      {
        title: 'Native Practicums integration',
        text: 'Simulated clinical encounters connect directly to the curriculum, so clinical skills practice and its assessment live in the same record.',
      },
    ],
    stats: null,
    deepDive: {
      heading: 'One login, one record, three applications',
      intro:
        'MedSchool is built as an institutional platform: a suite backbone, the PreRounds module, and simulated encounters, sharing one login, one database, and one competency framework.',
      items: [
        { title: 'The institutional backbone', text: 'Content library, learning management, a licensure-aligned question bank, and program analytics for course directors.' },
        { title: 'Competency across domains', text: 'Assessment events are tagged to a framework spanning the doing (constructing effective clinical prompts, integrating tools at the point of care), the judgment (calibrating trust, recognizing failure and bias), and the knowledge (fundamentals, data, ethics, and law).' },
        { title: 'The Failure Lab', text: 'Students study how clinical AI fails, deliberately: recognizing hallucination, bias, and overreach is treated as an examinable skill.' },
        { title: 'Evidence for accreditors', text: 'Every assessment event lands in a record designed to answer the question schools increasingly face: show us how you taught this.' },
      ],
    },
    exampleHeading: 'The Brownsville scenario',
    example:
      'An uninsured seven-year-old presents with otitis externa. The AI keeps recommending Ciprodex, a prescription the family cannot afford, and the learner either catches it or does not. Every session is scored against a clinical AI competency rubric, progress rolls into a competency profile, and completed training is CME-eligible: supervising AI becomes a skill that is measured and credentialed, not assumed.',
    media: {
      video: null,
      videoTitle: 'See PreRounds in action',
      videoText: 'A learner works a case with the AI assistant, TheDude intervenes on a real failure mode, and the session lands in the competency record.',
      screenshots: [
        { src: '/screenshots/prerounds.jpg', alt: 'PreRounds case view', caption: 'A PreRounds case workup' },
        { src: '/screenshots/pre-dash.jpg', alt: 'PreRounds dashboard', caption: 'The student dashboard' },
        { src: '/screenshots/pre-aiter.jpg', alt: 'AI interaction view', caption: 'Clinical AI use, captured and assessed' },
      ],
    },
    cta: { label: 'Bring it to your school', to: '/contact' },
    related: ['practicums', 'suites'],
  },
  {
    slug: 'practicums',
    name: 'Practicums',
    audienceTag: 'For students and programs',
    headline: 'The exam room, simulated.',
    subhead: 'A patient who converses. An evaluator who scores. Separately.',
    body: [
      'Practicums places students in AI-simulated patient encounters with photorealistic patient avatars and natural spoken conversation. Two independent intelligences run every encounter: a Patient AI that stays in character, and a separate Evaluator AI that independently scores clinical reasoning against structured criteria.',
      'Programs get OSCE-style clinical skills assessment capacity without standardized-patient scheduling and cost.',
    ],
    features: [
      {
        title: 'A patient you talk to',
        text: 'Photorealistic patient avatars with natural spoken conversation. The patient stays in character: they answer what you ask, and only what you ask.',
      },
      {
        title: 'An evaluator that scores separately',
        text: 'Clinical reasoning is scored by an independent Evaluator AI against structured criteria, separate from the patient simulation. The actor never grades the candidate.',
      },
      {
        title: 'Structured cases, physical exam included',
        text: 'A physical exam overlay and structured case architecture, so an encounter tests focused history, examination, and reasoning the way an OSCE station does.',
      },
    ],
    stats: null,
    deepDive: {
      heading: 'Why the separation matters',
      intro:
        'In a traditional OSCE, the standardized patient portrays and the physician examiner marks. Practicums keeps that separation of roles in software.',
      items: [
        { title: 'The Patient AI', text: 'Briefed on one person: their history, their symptoms, their personality, their worries. It converses in real time and does not volunteer what the candidate fails to elicit.' },
        { title: 'The Evaluator AI', text: 'Reads the full encounter afterward and scores it against structured, per-domain criteria: what was asked, what was examined, what was reasoned, what was missed.' },
        { title: 'The instructor workspace', text: 'A review workspace with the transcript, a per-domain scorecard with instructor override, and a chat where the instructor can question the evaluator about its own reasoning.' },
        { title: 'Honest scope', text: 'Practicums is assessment capacity for practice and formative evaluation. Validation against human examiners is the standard the product is built to meet, and instructor override is a permanent part of the design, not a temporary one.' },
      ],
    },
    exampleHeading: 'A station, start to finish',
    example:
      'Outside the door: a short briefing. A 74-year-old woman, three weeks of worsening breathlessness, eight minutes, focused history and examination. Inside: a patient on screen who answers in her own voice, hesitates where a patient would hesitate, and does not volunteer her medication list unless asked. Afterward, a structured scorecard: what was elicited, what was examined, what was missed, and why it mattered.',
    media: {
      video: null,
      videoTitle: 'Watch a simulated encounter',
      videoText: 'A recorded encounter with a simulated patient, followed by the evaluator scorecard and the instructor review workspace.',
      screenshots: [
        { src: '/screenshots/practicums.jpg', alt: 'Simulated patient encounter', caption: 'A simulated patient encounter' },
        { src: '/screenshots/prac-h.jpg', alt: 'Patient avatar conversation', caption: 'Natural spoken conversation' },
        { src: '/screenshots/prac-j.jpg', alt: 'Encounter review', caption: 'The encounter, reviewed' },
      ],
    },
    cta: { label: 'See a simulated encounter', to: '/contact' },
    related: ['medschool', 'prep'],
  },
  {
    slug: 'pathways',
    name: 'Pathways',
    audienceTag: 'For internationally trained physicians and their sponsors',
    headline: 'Every route you qualify for. Side by side.',
    subhead:
      'Licensure pathway comparison sourced to the regulator, including the routes you cannot take and why.',
    body: [
      'Internationally trained physicians make decisions worth years and thousands of dollars with fragmented, optimistic information. Pathways shows every route to a destination side by side, with time, cost, exam steps, and outcome, personalized to your training country, experience, and language status.',
      'Routes you do not qualify for are shown too, greyed, with the reason: the honesty no forum or agency will give you. Commit to a route and it becomes a personalized, dependency-ordered plan with a document vault, deadline reminders, and official sources on every step.',
    ],
    features: [
      {
        title: 'Side-by-side comparison',
        text: 'Every route to your destination with eligibility, time, cost, exam count, and the license you end up holding. Nothing hidden, including ineligibility.',
      },
      {
        title: 'Personalized step plans',
        text: 'Only the steps that apply to you, in dependency order. Your plan is frozen when you commit; if the underlying route changes, you see a banner and choose when to look.',
      },
      {
        title: 'A document vault',
        text: 'Upload once, fulfill every step that needs it, across every journey you ever start. Ten tracked document types, checked off automatically.',
      },
    ],
    stats: [
      { value: '8', label: 'Destination countries with published routes' },
      { value: '15', label: 'Explicitly modeled routes' },
      { value: '81', label: 'Sourced steps, every one citing the regulator' },
      { value: '87', label: 'Training countries recognized in matching' },
    ],
    deepDive: {
      heading: 'A reference product, not an opinion',
      intro:
        'Regulators publish requirements. They do not publish routes, they rarely tell you that you are ineligible, and they never compare themselves to the alternative next door. Pathways does all three, with the receipts.',
      items: [
        { title: 'Routes are explicit, versioned objects', text: 'Every route is a named entity with an eligibility rule, an ordered step list, a cost range, a time range, and a stated outcome. The same profile always produces the same answer, and the answer can be audited.' },
        { title: 'Every step is sourced and dated', text: 'Each step carries links to the regulator\u2019s own pages and a verified-on date: 95 official citations across 44 official domains, at least one on every step.' },
        { title: 'Ineligibility is a feature', text: 'Telling somebody early that they do not qualify for a route, and why, is worth more than any amount of encouragement.' },
        { title: 'Coverage today', text: 'United States, Canada, United Kingdom, Australia, New Zealand, UAE, Saudi Arabia, and Qatar; 78 jurisdictions modeled, route timelines from 3 months to 7 years, route costs from about USD 1,200 to USD 20,000 (estimates, sourced).' },
      ],
    },
    exampleHeading: 'What a comparison looks like',
    example:
      'A physician trained in one of 87 recognized countries picks a destination and answers four questions about her profile. Every route appears side by side. Two are open to her, with time, cost, and exam steps. One is greyed out with the reason stated plainly: her postgraduate experience does not meet the threshold, sourced to the regulator page that says so. She commits to a route and gets a plan of only the steps that apply to her, in the order they must happen.',
    media: {
      video: null,
      videoTitle: 'See a route comparison',
      videoText: 'A walkthrough of a profile, the side-by-side comparison including greyed ineligible routes, and a committed step plan with the document vault.',
      screenshots: [
        { src: '/screenshots/pathways-routes.jpg', alt: 'Pathways route comparison for Canada', caption: 'Every route side by side, including the ones you do not qualify for and why' },
      ],
    },
    cta: { label: 'Compare your routes', to: '/contact' },
    related: ['prep', 'express'],
  },
  {
    slug: 'express',
    name: 'EdAI Express',
    audienceTag: 'For any learner, any domain',
    headline: 'The tools without the content.',
    subhead: 'Your own material, transformed into exam-tuned study tools in about a minute.',
    body: [
      'EdAI Express is the modality engine on its own: upload your material (a lecture deck, a chapter, your notes) and get back practice questions with explanations, a structured study guide, and Anki-ready flashcards, tuned to the exam you are preparing for and the cognitive level you need.',
      'The tools are domain-agnostic by design. Today they serve healthcare licensure candidates across the US, Commonwealth, and Gulf; the same engine serves law, engineering, and any professional certification domain.',
    ],
    features: [
      {
        title: 'From your own PDFs',
        text: 'MCQs with explanations, structured study guides, and Anki-compatible flashcards, generated from the material you already have, in about a minute.',
      },
      {
        title: 'Tuned to your exam',
        text: 'Exam-family and cognitive-level tuning, from recall to clinical application, across US, Commonwealth, and Gulf licensure exam families.',
      },
      {
        title: 'Export everything',
        text: 'Real Anki decks, PDF, and Markdown, at no extra cost. Your study material stays yours.',
      },
    ],
    stats: null,
    deepDive: {
      heading: 'Built for the material you already have',
      intro:
        'Candidates almost always have material: the slide deck their program gave them, a chapter from an expensive textbook, notes a senior resident shared. What they lack is time to turn it into practice questions, a night-before summary, and flashcards to drill between shifts.',
      items: [
        { title: 'Upload and choose', text: 'Upload a PDF, pick your exam family (USMLE, NCLEX-RN, MCCQE, PLAB, AMC, or the Gulf licensing exams), and pick the cognitive level you need.' },
        { title: 'Study in the app or take it with you', text: 'Work the questions and guides inside Express, or export a real Anki deck and drill on your phone.' },
        { title: 'Simple pricing', text: 'Pay-as-you-go credits or a subscription, with coupon-powered free access for partners and institutions.' },
      ],
    },
    exampleHeading: 'A chapter in, a study kit out',
    example:
      'A nursing candidate uploads a 40-page pharmacology chapter and selects NCLEX-RN at application level. About a minute later: practice questions in the exam\u2019s style with explanations, a structured study guide of the chapter\u2019s testable content, and a flashcard deck ready for Anki. The night-before summary and the between-shifts drill, from her own coursework.',
    scopeNote:
      'Express works on whatever you bring it, which means the output is only as good as your material. Express content is generated from your uploads, not from board-designated sources, and does not carry the provenance guarantees of EdAI\u2019s authority-derived products.',
    media: {
      video: null,
      videoTitle: 'See an upload, start to finish',
      videoText: 'A real upload: a lecture PDF in, and the questions, study guide, and Anki export it produces about a minute later.',
      screenshots: [],
    },
    cta: { label: 'Try it with your own material', to: 'https://edai-express.com', external: true },
    related: ['prep', 'pathways'],
  },
];

export const careerArc = [
  { stage: 'Student', label: 'MedSchool', slugs: ['medschool'] },
  { stage: 'Clinical skills', label: 'Practicums', slugs: ['practicums'] },
  { stage: 'Foreign medical graduate', label: 'Pathways', slugs: ['pathways'] },
  { stage: 'Licensure and certification candidate', label: 'Prep', slugs: ['prep'] },
  { stage: 'Diplomate and MOC', label: 'Suites', slugs: ['suites'] },
];

export function getProduct(slug) {
  return products.find((p) => p.slug === slug);
}

export default products;
